import { useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppBar, Toolbar, Typography, IconButton, Box, Menu, MenuItem } from '@mui/material'
import { Menu as MenuIcon, Logout as LogoutIcon } from '@mui/icons-material'
import { logoutUser } from '../../store/slices/authSlice'
import { RootState } from '../../store'
import { useQueryClient } from '@tanstack/react-query'
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext'
import UserAvatar from '../ui/UserAvatar'

type BreadcrumbEntry = { section?: string; page: string }

const routeMap: Record<string, BreadcrumbEntry> = {
  '/dashboard':                  { page: 'Dashboard' },
  '/companies':                  { page: 'Company Management' },
  '/bills':                      { section: 'Operations', page: 'Bills' },
  '/loadforms':                  { section: 'Operations', page: 'Load Forms' },
  '/targets':                    { section: 'Operations', page: 'Targets' },
  '/customers':                  { section: 'Master Data', page: 'Customers' },
  '/skus':                       { section: 'Master Data', page: 'SKUs' },
  '/areas':                      { section: 'Master Data', page: 'Areas' },
  '/users':                      { section: 'Master Data', page: 'Bookers' },
  '/expenses':                   { section: 'Finance', page: 'Expenses' },
  '/account-ledger':             { section: 'Finance', page: 'Account Ledger' },
  '/reports':                    { section: 'Finance', page: 'Reports' },
  '/reports/sales-tax':          { section: 'Finance', page: 'Sales Tax Report' },
  '/reports/pnl':                { section: 'Finance', page: 'Profit & Loss' },
  '/reports/ar-aging':           { section: 'Finance', page: 'A/R Aging' },
  '/reports/balance-sheet':      { section: 'Finance', page: 'Balance Sheet' },
  '/purchase-invoices':          { section: 'Purchasing', page: 'Purchase Invoices' },
  '/inventory':                  { section: 'Purchasing', page: 'Inventory' },
  '/settings/vendors':           { section: 'Purchasing', page: 'Vendors' },
}

const subPageLabels: Record<string, string> = {
  edit: 'Edit',
  new: 'New',
  ledger: 'Ledger',
  settlement: 'Settlement',
}

function resolveBreadcrumb(pathname: string): BreadcrumbEntry | null {
  if (routeMap[pathname]) return routeMap[pathname]

  const sorted = Object.keys(routeMap).sort((a, b) => b.length - a.length)
  for (const prefix of sorted) {
    if (pathname.startsWith(prefix + '/') || pathname.startsWith(prefix)) {
      const rest = pathname.slice(prefix.length).replace(/^\//, '')
      const segments = rest.split('/').filter(Boolean)
      const base = routeMap[prefix]

      const extra = segments
        .filter((s) => !/^[a-f0-9]{24}$/.test(s))
        .map((s) => subPageLabels[s] || s.charAt(0).toUpperCase() + s.slice(1))

      const pageParts = [base.page, ...extra].join(' / ')
      return { section: base.section, page: pageParts }
    }
  }
  return null
}

interface HeaderProps {
  onToggleSidebar: () => void
}

const Header = ({ onToggleSidebar }: HeaderProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { confirmLeave } = useUnsavedChanges()
  const { user } = useSelector((state: RootState) => state.auth)

  const breadcrumb = useMemo(() => resolveBreadcrumb(location.pathname), [location.pathname])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    if (!confirmLeave()) return
    handleMenuClose()
    await dispatch(logoutUser() as any)
    queryClient.clear()
    navigate('/login')
  }

  return (
    <AppBar position="static" elevation={0}>
      <Toolbar>
        <IconButton
          edge="start"
          aria-label="menu"
          sx={{ mr: 2, color: '#7a766f' }}
          onClick={onToggleSidebar}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
          {user?.role === 'dev' && !breadcrumb && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
              Company Management
            </Typography>
          )}
          {breadcrumb && (
            <>
              {breadcrumb.section && (
                <>
                  <Typography variant="body2" sx={{ color: '#7a766f', whiteSpace: 'nowrap' }}>
                    {breadcrumb.section}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#c8c3bb', mx: 0.25 }}>
                    /
                  </Typography>
                </>
              )}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c2a26', whiteSpace: 'nowrap' }}>
                {breadcrumb.page}
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {user && (
            <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 400, fontSize: '0.9375rem', maxWidth: { xs: 120, sm: 240 } }}>
              Welcome, {user.name}
            </Typography>
          )}
          <IconButton onClick={handleMenuOpen} aria-label="Open user menu" sx={{ p: 0.5 }}>
            <UserAvatar name={user?.name} />
          </IconButton>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {user && (
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e8e4dd', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#2c2a26' }}>
                {user.name}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#7a766f', textTransform: 'capitalize' }}>
                {user.role}
              </Typography>
            </Box>
          )}
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1, fontSize: '1.125rem', color: '#7a766f' }} />
            <Typography variant="body2">Logout</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}

export default Header
